import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Accessibility = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">♿ CSS Accessibility</h1>

      <p className="tutorial-intro">
        Web accessibility ensures that websites are <b>usable by everyone</b>,
        including people with disabilities. With CSS, you can improve
        readability, navigation, and interaction for all users.
      </p>

      {/* ✅ Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #ecfccb, #d9f99d)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#365314", marginBottom: "0.5rem" }}>
          🔑 Key Points
        </h2>
        <ul
          style={{
            lineHeight: "1.8",
            fontSize: "1.05rem",
            paddingLeft: "25px",
          }}
        >
          <li>
            Ensure <b>color contrast</b> meets WCAG guidelines (contrast ratio ≥
            4.5:1).
          </li>
          <li>
            Use <code>:focus</code> styles so keyboard users can navigate
            easily.
          </li>
          <li>
            Choose <b>readable fonts</b> and avoid very small font sizes.
          </li>
          <li>
            Use <code>rem</code> or <code>em</code> for scalable layouts.
          </li>
          <li>
            Provide <b>skip links</b> so users can bypass repetitive content.
          </li>
          <li>Make forms accessible with clear labels and focus indicators.</li>
        </ul>
      </div>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 1: High Contrast Text</h2>
        <CodeCopyBlock
          code={`.high-contrast {
  color: #111;
  background-color: #fff;
}
.low-contrast {
  color: #888;
  background-color: #eee;
}`}
          language="css"
        />

        <p
          className="high-contrast"
          style={{
            padding: "10px",
            backgroundColor: "#fff",
            color: "#111",
            marginTop: "1rem",
          }}
        >
          ✅ This text has good contrast.
        </p>
        <p
          className="low-contrast"
          style={{
            padding: "10px",
            backgroundColor: "#eee",
            color: "#888",
          }}
        >
          ⚠️ This text has poor contrast.
        </p>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 2: Focus States</h2>
        <CodeCopyBlock
          code={`a:focus, button:focus {
  outline: 3px solid #2563eb;
  outline-offset: 2px;
}`}
          language="css"
        />

        <div style={{ marginTop: "1rem" }}>
          <a
            href="#"
            style={{
              marginRight: "10px",
              padding: "6px 12px",
              border: "1px solid #2563eb",
              borderRadius: "6px",
              textDecoration: "none",
            }}
          >
            Link
          </a>
          <button
            style={{
              padding: "6px 12px",
              border: "1px solid #2563eb",
              borderRadius: "6px",
              background: "#2563eb",
              color: "white",
            }}
          >
            Button
          </button>
        </div>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 3: Readable Fonts</h2>
        <CodeCopyBlock
          code={`body {
  font-family: Arial, sans-serif;
  line-height: 1.6;
  font-size: 1rem;
}`}
          language="css"
        />

        <p
          style={{
            fontFamily: "Arial, sans-serif",
            lineHeight: "1.6",
            fontSize: "1rem",
            marginTop: "1rem",
          }}
        >
          This paragraph uses a readable font size and good line height.
        </p>
      </div>

      {/* Example 4 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 4: Accessible Form</h2>
        <CodeCopyBlock
          code={`label {
  display: block;
  margin: 6px 0;
  font-weight: bold;
}
input:focus {
  border: 2px solid #16a34a;
  outline: none;
}`}
          language="css"
        />

        <form
          style={{
            marginTop: "1rem",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            maxWidth: "300px",
          }}
        >
          <label htmlFor="email">Email Address:</label>
          <input
            id="email"
            type="email"
            style={{
              padding: "8px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />
          <button
            type="submit"
            style={{
              background: "#16a34a",
              color: "white",
              padding: "10px",
              border: "none",
              borderRadius: "6px",
            }}
          >
            Submit
          </button>
        </form>
      </div>

      {/* Example 5 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 5: Skip Links</h2>
        <CodeCopyBlock
          code={`.skip-link {
  position: absolute;
  left: -999px;
  top: auto;
  background: #2563eb;
  color: white;
  padding: 8px;
}
.skip-link:focus {
  left: 10px;
  top: 10px;
}`}
          language="css"
        />

        <a
          href="#maincontent"
          className="skip-link"
          style={{
            position: "absolute",
            left: "-999px",
            background: "#2563eb",
            color: "white",
            padding: "8px",
            borderRadius: "6px",
          }}
        >
          Skip to main content
        </a>
        <div id="maincontent" style={{ marginTop: "2rem" }}>
          <p>Main content area (skip link target).</p>
        </div>
      </div>

      {/* Example 6 */}
      <div className="tutorial-section">
        <h2 className="section-title">
          👨‍💻 Example 6: Responsive & Scalable Units
        </h2>
        <CodeCopyBlock
          code={`html { font-size: 16px; }
h1 { font-size: 2rem; }  /* Scales with user preference */
p { font-size: 1rem; }`}
          language="css"
        />

        <div style={{ marginTop: "1rem" }}>
          <h1>Responsive Heading</h1>
          <p>This text scales based on user font size preferences.</p>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Use tools like <b>Lighthouse</b>, <b>axe DevTools</b>, and <b>Wave</b>{" "}
          to audit your website’s accessibility. Small CSS changes can make a
          huge impact for all users.
        </p>
      </div>
    </div>
  );
};

export default CSS_Accessibility;
